// My Calendar — the employee's unified month view over the Workforce Calendar
// feed: leave, holidays, meetings, 1:1s, goal/review deadlines, assignments,
// loan repayments (own data only; the gateway aggregation is permission- and
// privacy-filtered server-side). Month navigation refetches the visible range,
// so past months work for every event type. If one source service is down the
// rest of the calendar still renders, with a non-blocking warning.
import { weekdayLabels, weekdayOffset } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import {
  AlertTriangle, Calendar, CalendarDays, ChevronLeft, ChevronRight, Clock, Loader2, Plus, Target, Video,
} from 'lucide-react';
import { cancelMyEvent } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  CALENDAR_TYPE_META, countdownLabel, eventsOnDay,
  type CalendarEventType, type WorkforceCalendarEvent,
} from '@/lib/calendarApi';
import { useCalendarFeed } from '@/features/calendar/useCalendarFeed';
import { loadHiddenTypes, saveHiddenTypes } from '@/features/calendar/calendarPrefs';
import { CalendarFilterLegend } from '@/features/calendar/CalendarFilterLegend';
import { EventDetailSheet } from '@/features/calendar/EventDetailSheet';
import { NewMeetingDialog, type EditingMeeting } from '@/features/calendar/NewMeetingDialog';
import { DayEventsSheet } from '@/features/calendar/DayEventsSheet';
import { CalendarSyncCard } from './CalendarSyncCard';

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
// LOCAL calendar date, computed on demand — the old module-scope constant was
// (a) frozen at bundle load and (b) the UTC date, so "today" was wrong for the
// whole session west of UTC and after any midnight (timezone program 2026-08).
const localTodayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PREFS_KEY = 'my';

export const MyCalendarPage: React.FC = () => {
  const { t } = useTranslation('timeoff');
  // ?date=YYYY-MM-DD (e.g. "Add to calendar" from a training session) opens
  // that month with the day sheet expanded.
  const [searchParams] = useSearchParams();
  const jumpTo = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('date') || '') ? (searchParams.get('date') as string) : null;
  const [cursor, setCursor] = useState(() => (jumpTo ? new Date(jumpTo + 'T12:00:00') : new Date()));
  const [selected, setSelected] = useState<WorkforceCalendarEvent | null>(null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(() => loadHiddenTypes(PREFS_KEY));
  const [newMeetingOpen, setNewMeetingOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<EditingMeeting | null>(null);
  const [dayDetail, setDayDetail] = useState<string | null>(jumpTo);
  const { events, warnings, loading, error, reload } = useCalendarFeed('mine', cursor);

  useEffect(() => { saveHiddenTypes(PREFS_KEY, hiddenTypes); }, [hiddenTypes]);
  const toggleType = (tp: CalendarEventType) =>
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(tp)) next.delete(tp); else next.add(tp);
      return next;
    });

  const availableTypes = useMemo(() => {
    const present = new Set(events.map((e) => e.type));
    return (Object.keys(CALENDAR_TYPE_META) as CalendarEventType[]).filter((tp) => present.has(tp));
  }, [events]);

  const visible = useMemo(
    () => events.filter((e) => !hiddenTypes.has(e.type)),
    [events, hiddenTypes],
  );

  const cancelMeeting = async (id: string) => {
    try {
      await cancelMyEvent(id);
      toast.success(t('mycalendar.meeting_cancelled', 'Meeting cancelled'));
      reload();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('mycalendar.meeting_cancel_failed', 'Could not cancel')), { duration: 6000 });
    }
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = weekdayOffset(new Date(year, month, 1).getDay());
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const goMonth = (delta: number) => setCursor(new Date(year, month + delta, 1));

  const fmtTime = (isoStr: string) => new Date(isoStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const fmtShort = (d: string) => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fmtDateTime = (isoStr: string) => new Date(isoStr).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const upcoming = (types: CalendarEventType[], n = 5) =>
    visible
      .filter((e) => types.includes(e.type) && String(e.endAt || e.startAt).slice(0, 10) >= localTodayIso())
      .sort((a, b) => a.startAt.localeCompare(b.startAt))
      .slice(0, n);

  const upcomingMeetings = upcoming(['meeting', 'one_on_one'], 6);
  const upcomingLeave = upcoming(['leave']);
  const upcomingHolidays = upcoming(['holiday']);
  const upcomingDeadlines = useMemo(
    () => visible
      .filter((e) => CALENDAR_TYPE_META[e.type]?.deadline && e.status !== 'completed')
      .sort((a, b) => a.startAt.localeCompare(b.startAt))
      .slice(0, 6),
    [visible],
  );

  const chipFor = (e: WorkforceCalendarEvent) => {
    const meta = CALENDAR_TYPE_META[e.type];
    const stateCls =
      e.status === 'pending' ? ' opacity-60 border border-dashed border-current'
      : e.status === 'overdue' || e.status === 'at_risk' ? ' ring-1 ring-red-400' : '';
    const label = e.allDay ? e.title : `${fmtTime(e.startAt)} ${e.title}`;
    return (
      <button
        key={e.id}
        type="button"
        onClick={() => setSelected(e)}
        className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate ${meta.chip}${stateCls}`}
        title={`${meta.label} · ${e.title}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-h2 text-charcoal">{t('mycalendar.title', 'My Calendar')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('mycalendar.subtitle_unified', 'Your time off, meetings, deadlines and assignments in one place.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNewMeetingOpen(true)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-600 text-white hover:bg-orange-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> {t('mycalendar.new_meeting', 'New meeting')}
        </button>
      </div>

      {(warnings.length > 0 || error) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-800 dark:text-amber-300" role="status">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            {error || warnings.join(' ')}
            <button type="button" onClick={reload} className="ml-2 underline font-medium">
              {t('mycalendar.retry', 'Retry')}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Calendar className="w-5 h-5" /> {MONTHS[month]} {year}
                </CardTitle>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month"
                    className="h-8 w-8 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 inline-flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setCursor(new Date())}
                    className="h-8 px-3 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 text-sm font-medium">{t('mycalendar.today', 'Today')}</button>
                  <button type="button" onClick={() => goMonth(1)} aria-label="Next month"
                    className="h-8 w-8 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 inline-flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {loading ? (
                <div className="py-16 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('mycalendar.loading', 'Loading…')}</div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {weekdayLabels().map((d) => (
                    <div key={d} className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">{d}</div>
                  ))}
                  {Array.from({ length: firstWeekday }, (_, i) => <div key={`b${i}`} className="min-h-[84px] rounded-lg bg-muted/40" />)}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const dIso = iso(year, month, day);
                    const dayEvents = eventsOnDay(visible, dIso);
                    const hasHoliday = dayEvents.some((e) => e.type === 'holiday');
                    const isToday = dIso === localTodayIso();
                    return (
                      <div key={day} className={`min-h-[84px] rounded-lg border p-1.5 ${isToday ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-500/[0.08]' : hasHoliday ? 'border-indigo-200 bg-indigo-50/40 dark:border-indigo-500/30 dark:bg-indigo-500/[0.08]' : 'border-border bg-card'}`}>
                        <button
                          type="button"
                          onClick={() => setDayDetail(dIso)}
                          aria-label={`Show all events on day ${day}`}
                          className={`text-xs font-medium mb-1 rounded px-1 -mx-1 hover:bg-muted ${isToday ? 'text-orange-600' : 'text-foreground'}`}
                        >
                          {day}
                        </button>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 4).map(chipFor)}
                          {dayEvents.length > 4 && (
                            <button
                              type="button"
                              onClick={() => setDayDetail(dIso)}
                              className="text-[10px] text-muted-foreground hover:text-orange-600 px-1 font-medium"
                            >
                              +{dayEvents.length - 4} more
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardContent className="pt-4 pb-4">
              <CalendarFilterLegend availableTypes={availableTypes} hiddenTypes={hiddenTypes} onToggle={toggleType} />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <CalendarSyncCard />

          {upcomingDeadlines.length > 0 && (
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><Target className="w-5 h-5" /> {t('mycalendar.deadlines', 'Deadlines')}</CardTitle></CardHeader>
              <CardContent className="pt-6 space-y-3">
                {upcomingDeadlines.map((e) => {
                  const cd = countdownLabel(e.startAt);
                  return (
                    <button key={e.id} type="button" onClick={() => setSelected(e)} className="w-full text-left group">
                      <div className="text-sm font-medium text-foreground truncate group-hover:text-orange-700">{e.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {typeof e.progress === 'number' && <span>{Math.round(e.progress)}% · </span>}
                        <span className={e.status === 'overdue' ? 'text-red-600 dark:text-red-400 font-medium' : ''}>{cd}</span>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><Video className="w-5 h-5" /> {t('mycalendar.my_meetings', 'My meetings')}</CardTitle></CardHeader>
            <CardContent className="pt-6">
              {loading ? (
                <div className="py-6 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : upcomingMeetings.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{t('mycalendar.no_meetings', 'No upcoming meetings. Ask Dana to schedule one.')}</div>
              ) : (
                <div className="space-y-3">
                  {upcomingMeetings.map((m) => (
                    <button key={m.id} type="button" onClick={() => setSelected(m)} className="w-full flex items-start gap-2 text-left group">
                      <span className={`mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${CALENDAR_TYPE_META[m.type].chip}`}>{fmtDateTime(m.startAt)}</span>
                      <span className="flex-1 min-w-0 text-sm text-foreground truncate group-hover:text-orange-700" title={m.title}>{m.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><Clock className="w-5 h-5" /> {t('mycalendar.upcoming_leave', 'My upcoming time off')}</CardTitle></CardHeader>
            <CardContent className="pt-6">
              {upcomingLeave.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{t('mycalendar.no_upcoming_leave', 'No upcoming time off.')}</div>
              ) : (
                <div className="space-y-3">
                  {upcomingLeave.map((e) => (
                    <button key={e.id} type="button" onClick={() => setSelected(e)} className="w-full flex items-start gap-3 text-left">
                      <span className={`mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${CALENDAR_TYPE_META.leave.chip}`}>
                        {e.status === 'pending' ? t('mycalendar.pending', 'Pending') : t('mycalendar.approved', 'Approved')}
                      </span>
                      <span className="flex-1 min-w-0 text-sm text-foreground">
                        {fmtShort(e.startAt)}{e.endAt && e.endAt.slice(0, 10) !== e.startAt.slice(0, 10) && ` – ${fmtShort(e.endAt)}`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><CalendarDays className="w-5 h-5" /> {t('mycalendar.upcoming_holidays', 'Upcoming holidays')}</CardTitle></CardHeader>
            <CardContent className="pt-6">
              {upcomingHolidays.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{t('mycalendar.no_upcoming_holidays', 'No upcoming holidays.')}</div>
              ) : (
                <div className="space-y-3">
                  {upcomingHolidays.map((h) => (
                    <div key={h.id} className="flex items-center gap-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${CALENDAR_TYPE_META.holiday.chip}`}>{fmtShort(h.startAt)}</span>
                      <div className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">{h.title}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <DayEventsSheet
        dayIso={dayDetail}
        events={dayDetail ? eventsOnDay(visible, dayDetail) : []}
        onClose={() => setDayDetail(null)}
        onSelect={(ev) => { setDayDetail(null); setSelected(ev); }}
      />
      <EventDetailSheet
        event={selected}
        onClose={() => setSelected(null)}
        onCancelMeeting={cancelMeeting}
        onEditMeeting={(ev) => {
          setEditingMeeting({
            id: ev.sourceId, title: ev.title, startAt: ev.startAt, endAt: ev.endAt, location: ev.location,
          });
          setNewMeetingOpen(true);
        }}
      />
      <NewMeetingDialog
        open={newMeetingOpen}
        editing={editingMeeting}
        onClose={() => { setNewMeetingOpen(false); setEditingMeeting(null); }}
        onCreated={reload}
      />
    </div>
  );
};

export default MyCalendarPage;
